<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-console, no-use-before-define, vue/no-unused-properties */
const props = defineProps({
  inboxId: { type: [String, Number], required: true },
  existenceStatus: { type: String, required: false, default: '' },
  folderStyleStatus: { type: String, required: false, default: '' },
  canStartMigration: { type: Boolean, required: false, default: false },
  hasLocalFiles: { type: Boolean, required: false, default: false },
});

const { t } = useI18n();

// removed last-execution toggle logic: progress/details display directly controlled by state

const loading = ref(false);
const error = ref(null);
const hasRun = ref(false);
const migrationProgress = ref({
  status: 'not_started',
  processed: 0,
  total: 0,
});
const migrationResult = ref(null);
let progressInterval = null;
const showLastExecution = ref(false);
// Flag para indicar que a migração atual foi iniciada via "Iniciar Nova Migração"
const isNewMigrationClicked = ref(false);
// Elapsed time tracking (hoisted declarations to avoid use-before-define)
const analysisDate = ref(null);
const elapsedTime = ref('0s');
let elapsedTimeInterval = null;
const startTime = ref(null);
const emitStorageMigrationStatus = status => {
  try {
    window.dispatchEvent(
      new CustomEvent('storage_migration:status', {
        detail: { inboxId: props.inboxId, status: status || 'not_started' },
      })
    );
  } catch (e) {
    // non-blocking
  }
};

watch(
  () => migrationProgress.value.status,
  status => {
    emitStorageMigrationStatus(status);
  },
  { immediate: true }
);

const percentText = computed(() => {
  const total = Number(migrationProgress.value.total) || 0;
  const processed = Number(migrationProgress.value.processed) || 0;
  if (!total) return '0% - 0 / 0';
  const percent = (processed / total) * 100;
  if (Number.isInteger(percent)) return `${percent}% - ${processed} / ${total}`;
  return `${percent.toFixed(1).replace('.', ',')}% - ${processed} / ${total}`;
});

const percentProgressComputed = computed(() => {
  const total = Number(migrationProgress.value.total) || 0;
  const processed = Number(migrationProgress.value.processed) || 0;
  if (!total) return '0%';
  const percent = (processed / total) * 100;
  return `${percent}%`;
});

const formatSize = size => {
  if (!size && size !== 0) return '0 B';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024)
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const startMigrationTitle = computed(() => {
  if (migrationProgress.value && migrationProgress.value.status === 'running')
    return t('INBOX_MGMT.STORAGE_MIGRATION.IN_PROGRESS');
  if (migrationProgress.value && migrationProgress.value.status === 'paused')
    return t('INBOX_MGMT.STORAGE_MIGRATION.PAUSED');
  if (
    props &&
    props.existenceStatus &&
    ['running', 'paused'].includes(props.existenceStatus)
  ) {
    return props.existenceStatus === 'running'
      ? t('INBOX_MGMT.STORAGE_ANALYSIS.WAIT_DETAILED_FINISH')
      : t('INBOX_MGMT.STORAGE_ANALYSIS.DETAILED_ANALYSIS_PAUSED');
  }
  if (
    props &&
    props.folderStyleStatus &&
    ['running', 'paused'].includes(props.folderStyleStatus)
  ) {
    return props.folderStyleStatus === 'running'
      ? t('INBOX_MGMT.STORAGE_FOLDER.IN_PROGRESS')
      : t('INBOX_MGMT.STORAGE_FOLDER.PAUSED');
  }
  return '';
});

const CATEGORIES = [
  { key: 'image', label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.IMAGE') },
  { key: 'video', label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.VIDEO') },
  { key: 'audio', label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.AUDIO') },
  {
    key: 'documents',
    label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.DOCUMENTS'),
  },
  { key: 'other', label: t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORIES.OTHER') },
];

const fileCategory = contentType => {
  if (!contentType) return 'other';
  const main = contentType.split('/')[0];
  if (['image', 'video', 'audio'].includes(main)) return main;
  if (main === 'application') {
    if (
      contentType.includes('pdf') ||
      contentType.includes('msword') ||
      contentType.includes('officedocument')
    )
      return 'documents';
    return 'other';
  }
  return 'other';
};

const localFoundSummary = computed(() => {
  const summary = {};
  CATEGORIES.forEach(c => {
    summary[c.key] = { count: 0, size: 0 };
  });
  if (!migrationResult.value || !migrationResult.value.details) return summary;
  // Count only files that are actually present locally (found_local = true), not migrated and not orphan
  const orphanKeys = new Set(
    (
      (migrationResult.value.local_orphans &&
        migrationResult.value.local_orphans.details) ||
      []
    ).map(o => o.key)
  );
  migrationResult.value.details.forEach(d => {
    const is_found_local = d.found_local === true;
    if (!is_found_local) return;
    // exclude known orphans
    if (d.key && orphanKeys.has(d.key)) return;
    if (d.key && orphanKeys.has(d.key)) return; // exclude known orphans
    const cat = fileCategory(d.content_type);
    summary[cat].count += 1;
    summary[cat].size += Number(d.byte_size || 0);
  });
  return summary;
});

const localOrphansSummary = computed(() => {
  const summary = {};
  CATEGORIES.forEach(c => {
    summary[c.key] = { count: 0, size: 0 };
  });
  if (
    !migrationResult.value ||
    !migrationResult.value.local_orphans ||
    !migrationResult.value.local_orphans.details
  )
    return summary;
  migrationResult.value.local_orphans.details.forEach(d => {
    const cat = fileCategory(d.content_type);
    summary[cat].count += 1;
    summary[cat].size += Number(d.byte_size || 0);
  });
  return summary;
});

// S3 summaries derived from migration results (migrated files)
const s3FoundSummary = computed(() => {
  const summary = {};
  CATEGORIES.forEach(c => {
    summary[c.key] = { count: 0, size: 0 };
  });
  if (!migrationResult.value || !migrationResult.value.details) return summary;
  migrationResult.value.details.forEach(d => {
    // consider as S3-found if migrated or service_name is not local
    const is_s3 =
      d.migrated || (d.service_name && d.service_name.toString() !== 'local');
    if (!is_s3) return;
    const cat = fileCategory(d.content_type);
    summary[cat].count += 1;
    summary[cat].size += Number(d.byte_size || 0);
  });
  return summary;
});

// s3OrphansSummary removed: not applicable during migration

// totalLocalCount removed: not used by the template (avoids no-unused-vars)

const startMigration = async () => {
  // Always double-check server-side analysis before starting to avoid stale state
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_analysis`
    );
    const serverLocalCount = Number(data.local?.total_count || 0);
    if (serverLocalCount === 0) {
      useAlert('Não há arquivos locais para migrar.');
      return;
    }
  } catch (e) {
    useAlert(
      'Falha ao verificar arquivos locais no servidor. Tente novamente.'
    );
    return;
  }

  loading.value = true;
  error.value = null;
  hasRun.value = true;
  migrationResult.value = null;
  // Mark that a new migration was explicitly started by the user
  isNewMigrationClicked.value = true;
  // Ensure last-execution view is visible when starting migration
  showLastExecution.value = true;
  try {
    // persist flag so it survives reloads/pauses
    try {
      await saveProgressToBackend();
    } catch (e) {
      /* non-blocking */
    }
    // reset timers/display
    analysisDate.value = null;
    elapsedTime.value = '0s';
    startTime.value = Date.now();
    clearInterval(elapsedTimeInterval);
    elapsedTimeInterval = setInterval(updateElapsedTime, 1000);

    // Immediately reflect running state so the progress bar becomes visible
    migrationProgress.value = {
      status: 'running',
      processed: 0,
      total: migrationProgress.value.total || 0,
    };

    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/start`
    );
    pollMigrationProgress();
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      t('INBOX_MGMT.STORAGE_MIGRATION.ERROR_START');
    loading.value = false;
    clearInterval(elapsedTimeInterval);
    // on error, reset the explicit-start flag
    isNewMigrationClicked.value = false;
  }
};

const pauseMigration = async () => {
  await axios.post(
    `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/pause`
  );
};

const resumeMigration = async () => {
  await axios.post(
    `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/resume`
  );
};

const stopMigration = async () => {
  try {
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/stop`
    );
    // Fetch the latest progress so UI shows the 'stopped' state
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/progress`
    );
    migrationProgress.value = data;
    // Clear polling/timers and UI result to indicate interruption
    clearInterval(progressInterval);
    clearInterval(elapsedTimeInterval);
    loading.value = false;
    // Clear detailed result since migration was interrupted
    migrationResult.value = null;
    // Reset explicit-start flag since process was stopped
    isNewMigrationClicked.value = false;
    hasRun.value = true;
  } catch (e) {
    error.value = e?.response?.data?.message || 'Failed to stop migration.';
  }
};

const restartMigration = async () => {
  await axios.post(
    `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/restart`
  );
  // mark as explicitly restarted (treat as new migration run)
  isNewMigrationClicked.value = true;
  // Ensure UI shows progress/result when restarting
  showLastExecution.value = true;
  pollMigrationProgress();
};

const pollMigrationProgress = async () => {
  loading.value = true;
  const update = async () => {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/progress`
    );
    migrationProgress.value = data;
    hasRun.value = true;
    // Use updated_at from progress to show last change time on the progress bar
    if (data.updated_at) {
      analysisDate.value = new Date(data.updated_at).toLocaleDateString(
        'pt-BR'
      );
    }

    // Save progress including elapsed time to backend for persistence
    await saveProgressToBackend();

    // If backend provided elapsed_time when finished, prefer it
    if (data.status === 'finished' && data.elapsed_time) {
      elapsedTime.value = data.elapsed_time;
    }

    if (data.status === 'finished') {
      clearInterval(progressInterval);
      clearInterval(elapsedTimeInterval);
      loading.value = false;
      fetchMigrationResult();
      // keep isNewMigrationClicked true so labels persist after successful finish
    } else if (data.status === 'error') {
      // On error, clear intervals and reset flag (user can re-start via button)
      clearInterval(progressInterval);
      clearInterval(elapsedTimeInterval);
      loading.value = false;
      // fetch result if any (silent)
      try {
        await fetchMigrationResult();
      } catch (e) {
        /* ignore */
      }
      isNewMigrationClicked.value = false;
    }
  };
  await update();
  clearInterval(progressInterval);
  progressInterval = setInterval(update, 1000);
};

async function fetchMigrationResult() {
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}`
    );
    migrationResult.value = data;

    // If the migration is finished but there were zero files to migrate, keep the
    // last-execution panel hidden by default (user can still toggle to view history).
    try {
      if (
        migrationProgress.value &&
        migrationProgress.value.status === 'finished' &&
        Number(data.total || 0) === 0 &&
        Number(data.migrated || 0) === 0
      ) {
        showLastExecution.value = false;
      }
    } catch (err) {
      // non-blocking
      error.value =
        err?.message || 'Failed to evaluate migration result visibility.';
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message || 'Failed to fetch migration result.';
  }
}

const toggleShowLastExecution = async () => {
  showLastExecution.value = !showLastExecution.value;
  if (showLastExecution.value) {
    // When showing, load cached result silently (do not start polling)
    await fetchMigrationResult();
  }
};

const downloadOrphans = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/download_orphans`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = `migration_orphans_inbox_${props.inboxId}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    error.value =
      e?.response?.data?.message || 'Failed to download orphans report.';
  }
};

const loadState = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/state`
    );
    migrationProgress.value = data;
    // show last update time
    if (data && data.updated_at) {
      analysisDate.value = new Date(data.updated_at).toLocaleString('pt-BR');
    }
    // restore elapsed time (display) if available
    if (data && data.elapsed_time) {
      elapsedTime.value = data.elapsed_time;
    }

    // Restore is_new_migration flag if backend provided it
    if (data && typeof data.is_new_migration !== 'undefined') {
      isNewMigrationClicked.value = !!data.is_new_migration;
    }

    // If backend says migration is running, clear any stale result and start polling
    if (data && data.status === 'running') {
      // ensure UI panel is visible so users see the in-progress run
      showLastExecution.value = true;
      migrationResult.value = null;
      // restore startTime from elapsed_time so elapsed timer resumes correctly
      if (data.elapsed_time) {
        const et = data.elapsed_time;
        let seconds = 0;
        if (et.includes('m')) {
          const match = et.match(/(\d+)m\s*(\d+)s/);
          if (match)
            seconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
        } else {
          seconds = parseInt(et.replace('s', ''), 10) || 0;
        }
        startTime.value = Date.now() - seconds * 1000;
        clearInterval(elapsedTimeInterval);
        elapsedTimeInterval = setInterval(updateElapsedTime, 1000);
      } else {
        startTime.value = Date.now();
        clearInterval(elapsedTimeInterval);
        elapsedTimeInterval = setInterval(updateElapsedTime, 1000);
      }
      await pollMigrationProgress();
      return;
    }

    // If paused, show the state (do not start polling but keep the UI showing)
    if (data && data.status === 'paused') {
      hasRun.value = true;
      // show the panel so users can see paused run
      showLastExecution.value = true;
      // keep migrationResult null so user sees progress/summary when they open details
      migrationResult.value = null;
      return;
    }

    // If finished, fetch the final result
    if (data && data.status === 'finished') {
      await fetchMigrationResult();
      hasRun.value = true;
    }
  } catch (e) {
    // ignore
  }
};

onMounted(() => {
  // Load state silently on mount
  loadState();
});

const refreshState = async () => {
  await loadState();
};

defineExpose({ refreshState });
// elapsed time tracking (similar to existence check)
// (declarations are hoisted earlier to avoid linting 'use-before-define')

function updateElapsedTime() {
  if (!startTime.value) return;
  const diff = Math.floor((Date.now() - startTime.value) / 1000);
  if (diff < 60) {
    elapsedTime.value = `${diff}s`;
  } else if (diff < 3600) {
    elapsedTime.value = `${Math.floor(diff / 60)}m ${diff % 60}s`;
  } else {
    elapsedTime.value = `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
  }
}

async function saveProgressToBackend() {
  try {
    await axios.post(
      `/api/v1/inboxes/${props.inboxId}/storage_migrations/${props.inboxId}/save_progress`,
      {
        processed: migrationProgress.value.processed,
        total: migrationProgress.value.total,
        status: migrationProgress.value.status,
        elapsedTime: elapsedTime.value,
        // persist whether this run was started explicitly as a "new migration"
        is_new_migration: !!isNewMigrationClicked.value,
      }
    );
  } catch (e) {
    error.value = e?.message || 'Failed to save migration progress.';
  }
}

onUnmounted(() => {
  clearInterval(progressInterval);
  clearInterval(elapsedTimeInterval);
});
</script>

<template>
  <div>
    <div class="grid grid-cols-3 items-center mb-2">
      <div class="flex items-center gap-2 justify-start">
        <h3 class="text-lg font-semibold text-n-brand">
          {{ t('INBOX_MGMT.STORAGE_MIGRATION.TITLE') }}
        </h3>
      </div>
      <div class="flex justify-center">
        <button
          class="inline-flex items-center gap-2 whitespace-nowrap px-3 py-1 rounded bg-slate-700 text-white text-sm font-medium hover:brightness-110"
          @click="toggleShowLastExecution"
        >
          {{
            showLastExecution
              ? isNewMigrationClicked
                ? t('INBOX_MGMT.STORAGE_ANALYSIS.HIDE_EXECUTION')
                : t('INBOX_MGMT.STORAGE_ANALYSIS.HIDE_LAST_EXECUTION')
              : isNewMigrationClicked
                ? t('INBOX_MGMT.STORAGE_ANALYSIS.SHOW_EXECUTION')
                : t('INBOX_MGMT.STORAGE_ANALYSIS.SHOW_LAST_EXECUTION')
          }}
        </button>
      </div>
      <div class="flex justify-end">
        <button
          :disabled="
            loading ||
            ['running', 'paused'].includes(migrationProgress.status) ||
            ['running', 'paused'].includes(existenceStatus) ||
            ['running', 'paused'].includes(folderStyleStatus)
          "
          :title="startMigrationTitle"
          class="inline-flex items-center gap-2 px-4 py-2 rounded bg-n-brand text-white text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0"
          @click="startMigration"
        >
          <svg
            v-if="loading"
            class="animate-spin h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          {{ t('INBOX_MGMT.STORAGE_MIGRATION.START') }}
        </button>
      </div>
    </div>

    <div v-if="hasRun" class="ml-0 mr-0 py-0 w-full">
      <div v-if="error" class="text-red-600">{{ error }}</div>
      <div v-else>
        <div class="w-full mt-4">
          <div
            v-if="
              showLastExecution &&
              ['running', 'paused', 'finished'].includes(
                migrationProgress.status
              )
            "
          >
            <div
              class="w-full bg-slate-700 rounded h-5 mb-1 relative overflow-hidden"
            >
              <div
                class="h-5 rounded transition-all duration-700"
                :class="
                  migrationProgress.status === 'finished'
                    ? 'bg-green-500'
                    : 'bg-n-brand'
                "
                :style="{ width: percentProgressComputed }"
              />
              <span
                class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold px-2 pointer-events-none"
                :class="
                  migrationProgress.status === 'finished'
                    ? 'text-green-900'
                    : 'text-white'
                "
              >
                {{ percentText }}
              </span>
            </div>

            <div class="grid grid-cols-3 items-center mb-1">
              <div />
              <div
                class="text-center text-xs text-slate-700 dark:text-slate-400"
              >
                <span v-if="analysisDate"
                  >{{ t('INBOX_MGMT.STORAGE_ANALYSIS.LAST_ANALYSIS') }}:
                  {{ analysisDate }}</span
                >
              </div>
              <div class="text-right text-xs">
                <span
                  v-if="migrationProgress.status === 'finished'"
                  class="text-green-400 font-semibold"
                  >{{ t('INBOX_MGMT.STORAGE_ANALYSIS.COMPLETED') }}</span
                >
              </div>
            </div>
            <div
              v-if="
                ['running', 'paused', 'finished'].includes(
                  migrationProgress.status
                )
              "
              class="text-center text-sm text-slate-700 dark:text-slate-400 mt-2"
            >
              {{ t('INBOX_MGMT.STORAGE_ANALYSIS.ELAPSED_TIME') }}:
              {{ elapsedTime }}
            </div>

            <div class="flex justify-between mt-2">
              <button
                v-if="migrationProgress.status === 'running'"
                class="px-4 py-2 bg-yellow-500 text-black !text-black dark:!text-black rounded hover:brightness-110"
                @click="pauseMigration"
              >
                <span class="text-black !text-black dark:!text-black">
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.PAUSE') }}
                </span>
              </button>
              <div v-if="migrationProgress.status === 'paused'">
                <button
                  class="px-4 py-2 bg-green-500 text-white rounded hover:brightness-110"
                  @click="resumeMigration"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.RESUME') }}
                </button>
                <button
                  class="inline-flex items-center gap-2 px-4 py-2 rounded bg-n-brand text-white text-sm font-medium hover:brightness-110 disabled:opacity-50 border-0 ml-2"
                  @click="restartMigration"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.RESTART') }}
                </button>
              </div>
              <button
                v-if="['running', 'paused'].includes(migrationProgress.status)"
                class="px-4 py-2 bg-red-500 text-white rounded hover:brightness-110"
                @click="stopMigration"
              >
                {{ t('INBOX_MGMT.STORAGE_ANALYSIS.STOP') }}
              </button>
            </div>
          </div>

          <div
            v-if="
              showLastExecution &&
              migrationResult &&
              migrationProgress.status === 'finished'
            "
            class="mt-4"
          >
            <h5 class="text-base font-semibold text-n-brand mb-2 text-center">
              {{ t('INBOX_MGMT.STORAGE_MIGRATION.SUMMARY') }}
            </h5>
            <div
              class="rounded-lg p-4 bg-white dark:bg-[rgb(35,38,43)] border border-slate-200 dark:border-[#35373c]"
            >
              <div
                class="text-center font-semibold"
                :class="[
                  migrationResult && migrationResult.total > 0
                    ? migrationResult.migrated === 0
                      ? 'text-red-600 dark:text-red-400'
                      : migrationResult.migrated < migrationResult.total
                        ? 'text-yellow-600 dark:text-yellow-300'
                        : 'text-green-600 dark:text-green-400'
                    : 'text-slate-900 dark:text-white',
                ]"
              >
                {{ t('INBOX_MGMT.STORAGE_MIGRATION.MIGRATED_LABEL') }}:
                {{ migrationResult.migrated }} / {{ migrationResult.total }}
              </div>
              <div class="mt-2 text-center" />
              <div
                class="mt-2 text-sm text-yellow-600 dark:text-yellow-300 text-center font-medium"
              >
                <span class="text-slate-900 dark:text-white font-medium">{{
                  t('INBOX_MGMT.STORAGE_MIGRATION.WARNING_TITLE')
                }}</span>
                <span class="ml-1 text-slate-700 dark:text-slate-200">{{
                  t('INBOX_MGMT.STORAGE_MIGRATION.WARN_LOCAL_COPY')
                }}</span>
              </div>
              <!-- download button moved below Local summary -->
            </div>
          </div>
          <!-- Grid resumo Local | S3 (per-category rows) -->
          <div
            v-if="
              showLastExecution &&
              migrationResult &&
              migrationProgress.status === 'finished'
            "
            class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4"
          >
            <!-- Local -->
            <div class="h-full">
              <div
                class="rounded-lg p-4 flex flex-col h-full bg-white dark:bg-[rgb(35,38,43)] border border-slate-200 dark:border-[#35373c]"
              >
                <div
                  class="font-semibold text-n-brand mb-2 text-lg text-center"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.LOCAL') }}
                </div>
                <div class="overflow-x-auto flex-1">
                  <div class="w-full text-xs">
                    <div
                      class="grid grid-cols-3 gap-2 items-center py-2 bg-slate-100 dark:bg-slate-800 rounded-t px-3"
                    >
                      <div class="font-semibold">
                        {{ t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORY') }}
                      </div>
                      <div class="text-center text-green-600 font-semibold">
                        {{ t('INBOX_MGMT.STORAGE_MIGRATION.FOUND') }}
                      </div>
                      <div class="text-right text-red-600 font-semibold">
                        {{ t('INBOX_MGMT.STORAGE_MIGRATION.LOST') }}
                      </div>
                    </div>
                    <div
                      class="divide-y divide-slate-200 dark:divide-slate-700"
                    >
                      <div
                        v-for="cat in CATEGORIES"
                        :key="cat.key + '-row'"
                        class="grid grid-cols-3 items-center py-2 px-3"
                      >
                        <div
                          class="font-semibold text-slate-900 dark:text-white"
                        >
                          {{ cat.label }}
                        </div>
                        <div class="text-center text-green-700 font-semibold">
                          {{ localFoundSummary[cat.key].count }}
                          {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES')
                          }}<br /><span
                            class="text-xs text-slate-500 dark:text-slate-400"
                            >({{
                              formatSize(localFoundSummary[cat.key].size)
                            }})</span
                          >
                        </div>
                        <div class="text-right text-red-700 font-semibold">
                          {{ localOrphansSummary[cat.key].count }}
                          {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES')
                          }}<br /><span
                            class="text-xs text-slate-500 dark:text-slate-400"
                            >({{
                              formatSize(localOrphansSummary[cat.key].size)
                            }})</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mt-3 flex justify-center">
                  <button
                    v-if="
                      migrationResult &&
                      migrationResult.local_orphans &&
                      migrationResult.local_orphans.details &&
                      migrationResult.local_orphans.details.length > 0
                    "
                    class="px-3 py-1 bg-slate-700 text-white rounded text-sm hover:brightness-110"
                    @click="downloadOrphans"
                  >
                    {{
                      t('INBOX_MGMT.STORAGE_MIGRATION.DOWNLOAD_ORPHANS_LABEL')
                    }}
                  </button>
                </div>
              </div>
            </div>
            <!-- S3 -->
            <div class="h-full">
              <div
                class="rounded-lg p-4 flex flex-col h-full bg-white dark:bg-[rgb(35,38,43)] border border-slate-200 dark:border-[#35373c]"
              >
                <div
                  class="font-semibold text-n-brand mb-2 text-lg text-center"
                >
                  {{ t('INBOX_MGMT.STORAGE_ANALYSIS.S3_STORAGE') }}
                </div>
                <div class="overflow-x-auto flex-1">
                  <div class="w-full text-xs">
                    <div
                      class="grid grid-cols-2 gap-2 items-center py-2 bg-slate-100 dark:bg-slate-800 rounded-t px-3"
                    >
                      <div class="font-semibold">
                        {{ t('INBOX_MGMT.STORAGE_MIGRATION.CATEGORY') }}
                      </div>
                      <div class="text-center text-green-600 font-semibold">
                        {{ t('INBOX_MGMT.STORAGE_MIGRATION.FOUND_S3') }}
                      </div>
                    </div>
                    <div
                      class="divide-y divide-slate-200 dark:divide-slate-700"
                    >
                      <div
                        v-for="cat in CATEGORIES"
                        :key="cat.key + '-s3-row'"
                        class="grid grid-cols-2 items-center py-2 px-3"
                      >
                        <div
                          class="font-semibold text-slate-900 dark:text-white"
                        >
                          {{ cat.label }}
                        </div>
                        <div class="text-center text-green-700 font-semibold">
                          {{ s3FoundSummary[cat.key].count }}
                          {{ t('INBOX_MGMT.STORAGE_ANALYSIS.FILES')
                          }}<br /><span
                            class="text-xs text-slate-500 dark:text-slate-400"
                            >({{
                              formatSize(s3FoundSummary[cat.key].size)
                            }})</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- keep footer space aligned with Local block; invisible placeholder when no action -->
                <div class="mt-3 flex justify-center">
                  <button
                    v-if="false"
                    class="px-3 py-1 bg-slate-700 text-white rounded text-sm hover:brightness-110 invisible"
                  >
                    placeholder
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- Detalhes da Migração removidos conforme solicitacao do usuario -->
        </div>
      </div>
    </div>
  </div>
</template>
